'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { LANGUAGES, type LanguageCode, type TestDuration } from '@/data/wordLists';

interface TestConfigValue {
  language: LanguageCode;
  duration: TestDuration;
  setLanguage: (l: LanguageCode) => void;
  setDuration: (d: TestDuration) => void;
}

const TestConfigContext = createContext<TestConfigValue | null>(null);

export function TestConfigProvider({
  children,
  initialLanguage = 'english',
  initialDuration = 60,
}: {
  children: React.ReactNode;
  /** Preset language for dedicated SEO routes (e.g. /hindi-typing-test). */
  initialLanguage?: LanguageCode;
  initialDuration?: TestDuration;
}) {
  const [language, setLanguage] = useState<LanguageCode>(initialLanguage);
  const [duration, setDuration] = useState<TestDuration>(initialDuration);

  // A ?lang= / ?duration= query param still overrides the preset (deep links).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang') as LanguageCode | null;
    const durParam = params.get('duration');
    if (langParam && LANGUAGES.find((l) => l.code === langParam)) setLanguage(langParam);
    if (durParam) {
      const d = parseInt(durParam) as TestDuration;
      if ([30, 60, 120, 300].includes(d)) setDuration(d);
    }
  }, []);

  return (
    <TestConfigContext.Provider value={{ language, duration, setLanguage, setDuration }}>
      {children}
    </TestConfigContext.Provider>
  );
}

export function useTestConfig() {
  const ctx = useContext(TestConfigContext);
  if (!ctx) throw new Error('useTestConfig must be used within a TestConfigProvider');
  return ctx;
}
